import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Link2, RefreshCw, Unplug } from 'lucide-react';
import { api } from '../../api';
import { pode, useSessao } from '../../sessao';
import { Modal } from '../../componentes/Modal';
import { EstoqueMaza } from './EstoqueMaza';
import { TokensServico } from './TokensServico';

interface Conexao {
  id: string; empresa_faturadora_id: string | null; empresa: string | null; conta_nome: string | null; conta_documento: string | null;
  status: 'ativa' | 'erro' | 'reconectar'; ultima_sync_em: string | null; ultimo_erro: string | null; criado_em: string; conectado_por: string | null; clientes_ligados: number;
}
interface Execucao {
  id: string; conexao_id: string; origem: string; iniciada_em: string; terminada_em: string | null; status: string;
  lidos: number; criados: number; vinculados: number; atualizados: number; sem_mudanca: number; avisos: number; erro: string | null;
}
interface Dados { conta_azul_configurado: boolean; conexoes: Conexao[]; empresas: { id: string; apelido: string; razao_social: string }[]; execucoes: Execucao[] }

const dataHora = (iso: string) => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
const NOME_STATUS: Record<string, string> = { ativa: 'Conectado', erro: 'Erro na última sincronização', reconectar: 'Precisa conectar de novo' };
const NOME_EXEC: Record<string, string> = { rodando: 'Rodando', ok: 'OK', parcial: 'Com avisos', erro: 'Erro' };

/**
 * Integrações. Conta Azul = dono do cadastro fiscal do cliente (razão social, CPF/CNPJ, ativo/inativo).
 * O Ops importa e mantém atualizado; nome de uso, locais, contatos e equipamentos continuam sendo do Ops.
 */
export function Integracoes() {
  const { usuario } = useSessao();
  const admin = pode(usuario, 'integracoes.gerir');
  const [params, setParams] = useSearchParams();
  const [d, setD] = useState<Dados | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [empresa, setEmpresa] = useState('');
  const [avisos, setAvisos] = useState<{ id: string; lista: { nome: string; motivo: string }[] } | null>(null);

  const carregar = useCallback(() => { api<Dados>('/integracoes').then(setD).catch((e) => setErro(e.message)); }, []);
  useEffect(() => { carregar(); }, [carregar]);
  // enquanto a primeira importação roda em segundo plano, atualiza sozinho
  useEffect(() => {
    if (!d?.execucoes.some((e) => e.status === 'rodando')) return;
    const t = setInterval(carregar, 3000);
    return () => clearInterval(t);
  }, [d, carregar]);

  const retorno = params.get('conta_azul');
  const motivo = params.get('motivo');

  async function conectar() {
    setErro(null); setOcupado('conectar');
    try {
      const r = await api<{ url: string }>('/integracoes/conta-azul/conectar', { metodo: 'POST', corpo: { empresa_faturadora_id: empresa || null } });
      window.location.assign(r.url);   // vai para o login do Conta Azul e volta em /api/integracoes/conta-azul/retorno
    } catch (e) { setErro((e as Error).message); setOcupado(null); }
  }
  async function sincronizar(id: string) {
    setErro(null); setOcupado(id);
    try { await api(`/integracoes/${id}/sincronizar`, { metodo: 'POST' }); } catch (e) { setErro((e as Error).message); }
    finally { setOcupado(null); carregar(); }
  }
  async function desconectar(c: Conexao) {
    if (!window.confirm('Desconectar o Conta Azul? Os clientes já importados ficam, mas param de ser atualizados.')) return;
    try { await api(`/integracoes/${c.id}`, { metodo: 'DELETE' }); carregar(); } catch (e) { setErro((e as Error).message); }
  }
  async function verAvisos(id: string) {
    const r = await api<{ avisos: { nome: string; motivo: string }[] }>(`/integracoes/execucoes/${id}`);
    setAvisos({ id, lista: r.avisos });
  }

  return (
    <>
      <div className="cabecalho-pagina">
        <div>
          <span className="kicker">Configuração</span>
          <h1 className="titulo">Integrações</h1>
        </div>
      </div>

      {retorno === 'conectado' && <p className="alerta alerta-ok" role="status">Conta Azul conectado. A primeira importação de clientes está rodando — acompanhe abaixo. <button className="btn-link" onClick={() => setParams({})}>fechar</button></p>}
      {retorno === 'erro' && <p className="alerta" role="alert">Não conectou: {motivo ?? 'autorização não concluída'}. <button className="btn-link" onClick={() => setParams({})}>fechar</button></p>}
      {erro && <p className="alerta" role="alert">{erro}</p>}

      <section className="cartao integracao">
        <header className="bloco-topo">
          <div>
            <h2>Conta Azul</h2>
            <p className="nota">
              Clientes vêm do ERP: <strong>razão social, CPF/CNPJ e situação</strong> passam a ser do Conta Azul (corrija lá).
              Nome de uso, parques, contatos e equipamentos continuam no Ops. Atualiza a cada 30 minutos; o endereço do ERP só
              vira parque na primeira importação (em condomínio ele costuma ser o de cobrança).
            </p>
          </div>
        </header>

        {!d ? <p className="nota">Carregando…</p> : (
          <>
            {!d.conta_azul_configurado && (
              <p className="alerta alerta-aviso">O aplicativo do Conta Azul ainda não foi instalado no servidor (credenciais do Portal do Desenvolvedor). Sem isso não dá para conectar.</p>
            )}
            {d.conexoes.length === 0 ? <p className="nota">Nenhuma conta conectada.</p> : (
              <div className="rolagem-x"><table className="tabela">
                <thead><tr><th>Conta</th><th>Situação</th><th className="num">Clientes ligados</th><th>Última sincronização</th>{admin && <th aria-label="Ações" />}</tr></thead>
                <tbody>
                  {d.conexoes.map((c) => (
                    <tr key={c.id}>
                      <td><strong>{c.conta_nome ?? 'Conta Azul'}</strong>{c.empresa && <small>faturadora: {c.empresa}</small>}<small>conectado por {c.conectado_por ?? '—'} em {dataHora(c.criado_em)}</small></td>
                      <td><span className={`situacao ${c.status === 'ativa' ? 's-ok' : 's-nok'}`}>{NOME_STATUS[c.status]}</span>{c.ultimo_erro && <small>{c.ultimo_erro}</small>}</td>
                      <td className="num">{c.clientes_ligados}</td>
                      <td>{c.ultima_sync_em ? dataHora(c.ultima_sync_em) : '—'}</td>
                      {admin && (
                        <td className="acoes acoes-linha">
                          {c.status === 'reconectar' ? <button className="btn-fantasma" onClick={conectar}><Link2 size={15} aria-hidden /> Conectar de novo</button>
                            : <button className="btn-fantasma" disabled={ocupado === c.id} onClick={() => sincronizar(c.id)}><RefreshCw size={15} aria-hidden /> {ocupado === c.id ? 'Sincronizando…' : 'Sincronizar agora'}</button>}
                          <button className="btn-icone" onClick={() => desconectar(c)} aria-label="Desconectar"><Unplug size={16} /></button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
            {admin && d.conta_azul_configurado && (
              <div className="linha-conectar">
                {d.empresas.length > 1 && (
                  <label>Empresa faturadora desta conta
                    <select value={empresa} onChange={(e) => setEmpresa(e.target.value)}>
                      <option value="">Conta única (as duas empresas)</option>
                      {d.empresas.map((e) => <option key={e.id} value={e.id}>{e.razao_social}</option>)}
                    </select>
                  </label>
                )}
                <button className="btn" disabled={ocupado === 'conectar'} onClick={conectar}><Link2 size={18} aria-hidden /> {ocupado === 'conectar' ? 'Abrindo o Conta Azul…' : d.conexoes.length ? 'Conectar outra conta' : 'Conectar conta do Conta Azul'}</button>
              </div>
            )}
          </>
        )}
      </section>

      {d && d.execucoes.length > 0 && (
        <section className="secao">
          <h2>Histórico do Conta Azul</h2>
          <div className="cartao tabela-cartao rolagem-x">
            <table className="tabela">
              <thead><tr><th>Quando</th><th>Situação</th><th className="num">Lidos</th><th className="num">Novos</th><th className="num">Ligados</th><th className="num">Atualizados</th><th className="num">Avisos</th></tr></thead>
              <tbody>
                {d.execucoes.map((e) => (
                  <tr key={e.id}>
                    <td>{dataHora(e.iniciada_em)}<small>{e.origem === 'manual' ? 'manual' : 'automática'}</small></td>
                    <td><span className={`situacao ${e.status === 'ok' ? 's-ok' : e.status === 'rodando' ? 's-na' : 's-nok'}`}>{NOME_EXEC[e.status]}</span>{e.erro && <small>{e.erro}</small>}</td>
                    <td className="num">{e.lidos}</td>
                    <td className="num">{e.criados}</td>
                    <td className="num">{e.vinculados}</td>
                    <td className="num">{e.atualizados}</td>
                    <td className="num">{e.avisos > 0 ? <button className="btn-link" onClick={() => verAvisos(e.id)}>{e.avisos}</button> : 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="nota">Ligados = já existia no Ops com o mesmo CPF/CNPJ e passou a ser atualizado pelo Conta Azul (não duplica).</p>
        </section>
      )}

      <div className="secao"><EstoqueMaza admin={admin} verAvisos={verAvisos} /></div>

      {admin && <TokensServico />}

      <Modal titulo="Avisos da sincronização" aberto={Boolean(avisos)} aoFechar={() => setAvisos(null)}>
        {avisos && (
          <ul className="lista-simples lista-avisos">
            {avisos.lista.map((a, i) => <li key={i}><strong>{a.nome}</strong><small>{a.motivo}</small></li>)}
          </ul>
        )}
      </Modal>
    </>
  );
}
